<template>
  <div class="cart__payment-options cart__payment-options--stripe">
    <div id="express-checkout-element"></div>
    <div v-show="isLoading" class="stripe-button-loading">
      <three-dots-loading />
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */

import ThreeDotsLoading from '~/components/loadings/three-dots/ThreeDotsLoading.vue'
import loadScript from '~/utils/loadScript'
import appendUrlParam from '~/utils/appendUrlParam'

export default {
  name: 'StripeCheckout',
  components: {
    ThreeDotsLoading,
  },
  props: {
    orderNumber: {
      type: [Number, String],
      required: true,
    },
    itemsTotal: {
      type: [Number, String],
      required: true,
    },
    publishableKey: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isLoading: true,
      paymentIntent: null,
      loadingButtons: true,
      elements: null,
    }
  },
  watch: {
    itemsTotal(newPrice) {
      this.elements.update({
        amount: this.parseAmount(newPrice),
      })
    },
  },
  mounted() {
    if (!this.publishableKey) return
    loadScript('https://js.stripe.com/v3')
      .then(() => {
        this.finalizePayment()
        this.loadExpressButtons(this.publishableKey)
      })
      .catch((err) => {
        console.log('error stripe', err)
        this.$sentry.captureException(err?.response?.data?.message || err)
      })
  },
  methods: {
    async finalizePayment() {
      const {
        // eslint-disable-next-line camelcase
        payment_intent,
        // eslint-disable-next-line camelcase
        payment_intent_client_secret,
        // eslint-disable-next-line camelcase
        redirect_status,
      } = this.$route.query

      if (
        // eslint-disable-next-line camelcase
        payment_intent &&
        // eslint-disable-next-line camelcase
        payment_intent_client_secret &&
        // eslint-disable-next-line camelcase
        redirect_status === 'succeeded'
      ) {
        this.loadingButtons = false
        this.$emit('showLoading')
        await this.paymentSuccess(payment_intent)
      }
    },
    removeStripeBtn() {
      document.querySelector('.cart__payment-options--stripe').remove()
    },
    // Function for loading express checkout buttons
    loadExpressButtons(publishableKey) {
      if (!this.loadingButtons) return

      // Instantiate Stripe with publishable key and API version
      const stripe = window.Stripe(publishableKey, {
        apiVersion: '2022-11-15',
      })

      // Options for the express checkout button
      const options = {
        mode: 'payment',
        amount: this.parseAmount(this.itemsTotal), // parsed total items amount
        currency: this.$store?.state?.currency?.toLowerCase(), // set currency from store state
        captureMethod: 'automatic', // set capture method to automatic
      }

      // Create Stripe elements with provided options
      this.elements = stripe.elements(options)

      // Create express checkout button
      const expressCheckoutElement = this.elements.create('expressCheckout', {
        layout: {
          maxRows: 5,
        },
      })

      // Handle click event on express checkout button
      expressCheckoutElement.on('click', (event) => {
        const options = {
          shippingAddressRequired: true,
          emailRequired: true,
          phoneNumberRequired: true,
          shippingRates: [
            {
              id: 'select-shipping',
              displayName: 'Select a shipping method',
              amount: 0,
            },
          ],
        }
        // Resolve the click event with options
        event.resolve(options)
      })

      // Handle shipping address change event
      expressCheckoutElement.on('shippingaddresschange', async (event) => {
        // Create the API endpoint for shipping methods
        const PATH_SHIPPING_METHODS = appendUrlParam(
          `/stripe_buttons/shipment_methods`,
          {
            ...this.$store.getters.storeInfo, // pass store info to endpoint
            order_number: this.orderNumber, // pass order number to endpoint
          }
        )

        try {
          // Fetch shipping options from the API
          const shippingOptions = await this.$apiv2.$get(
            PATH_SHIPPING_METHODS,
            {
              params: {
                shipping_address: event.address, // pass shipping address to API
              },
            }
          )

          // Update Stripe elements with new amount after fetching shipping options
          this.elements.update({
            amount: this.parseAmount(shippingOptions.total),
          })

          // Resolve the event with shipping rates
          event.resolve({
            shippingRates: shippingOptions.options.map((shipping) => ({
              id: String(shipping.id),
              displayName: shipping.label_shipping_rate,
              amount: this.parseAmount(shipping.cost),
            })),
          })
        } catch (e) {
          console.log('[shippingaddresschange] event reject')
          // Reject the event if fetching shipping options fails
          event.reject()
        }
      })

      // Handle shipping rate change event
      expressCheckoutElement.on('shippingratechange', async (event) => {
        // Create the API endpoint for setting shipping rate
        const PATH_SET_SHIPPING = appendUrlParam(
          `/stripe_buttons/set_shipping_rate`,
          {
            ...this.$store.getters.storeInfo, // pass store info to endpoint
            order_number: this.orderNumber, // pass order number to endpoint
            rate_id: event.shippingRate.id, // pass selected shipping rate id to endpoint
          }
        )
        try {
          // Set the shipping rate via API
          const setShipping = await this.$apiv2.$put(PATH_SET_SHIPPING)

          // Update Stripe elements with new total amount after setting shipping rate
          this.elements.update({
            amount: this.parseAmount(setShipping.total),
          })

          // Resolve the event
          event.resolve()
        } catch (e) {
          console.log('[shippingratechange] event reject')
          // Reject the event if setting shipping rate fails
          event.reject()
        }
      })

      expressCheckoutElement.on('confirm', async (event) => {
        // Create a PaymentMethod using the details collected by the Express Checkout Element
        const { error: submitError } = await this.elements.submit()
        if (submitError) {
          this.dispatchError(submitError.message)
          return
        }

        const PATH = appendUrlParam(
          `/stripe_buttons/create/${this.orderNumber}`,
          {
            ...this.$store.getters.storeInfo,
          }
        )

        try {
          const { client_secret: clientSecret } = await this.$apiv2.$post(
            PATH,
            {
              address: {
                ...event.shippingAddress,
                phone: event.billingDetails?.phone,
              },
            }
          )

          const { error: stripeError } = await stripe.confirmPayment({
            // `elements` instance used to create the Express Checkout Element
            elements: this.elements,
            // `clientSecret` from the created PaymentIntent
            clientSecret,
            confirmParams: {
              return_url: window.location.href,
            },
          })

          if (stripeError) {
            // This point is only reached if there's an immediate error when
            this.dispatchError(stripeError.message)
          }
        } catch (err) {
          this.dispatchError(err?.response?.data?.message)
        }
      })

      // Mount the express checkout button to the DOM
      expressCheckoutElement.mount('#express-checkout-element')

      // Listen to the ready event
      expressCheckoutElement.on('ready', ({ availablePaymentMethods }) => {
        if (availablePaymentMethods) {
          // No buttons will show
          this.isLoading = false
        }
      })
    },
    parseAmount(stringAmount) {
      const fixed = parseFloat(stringAmount.replace(/[^\d|,|.]+/g, '')).toFixed(
        2
      )
      const valueStripe = parseFloat(fixed.replace(/[^0-9]/g, ''))
      return valueStripe
    },
    async paymentSuccess(paymentIntentId) {
      try {
        const PATH = appendUrlParam(
          `/stripe_buttons/finalize/${this.orderNumber}`,
          {
            ...this.$store.getters.storeInfo,
            payment_intent: paymentIntentId,
          }
        )
        this.removeStripeBtn()
        this.$displayOverlaidLoading()
        const response = await this.$axios.$get(PATH, {})
        window.location.replace(response.path)
      } catch (err) {
        // refund and redirect
        this.dispatchError(err?.response?.data?.message)
        setTimeout(() => {
          window.location.replace(
            `${window.location.origin}${window.location.pathname}?error=${err?.response?.data?.message}`
          )
        }, 1000)
      }
    },
    dispatchError(error) {
      this.$displayTopMessage(
        'An error occurred with this payment method. Please try again.',
        {
          type: 'error',
        }
      )
      this.$sentry.captureException({
        error: 'Stripe Button checkout fails: finalize order',
        params: {
          error,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.cart__payment-options--stripe {
  margin-top: 10px !important;
  min-height: 50px;

  .stripe-button-loading {
    min-height: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
  }

  #express-checkout-element {
    margin-top: 10px;
  }
}
</style>
